import React from "react";

import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import BusinessRoundedIcon from "@material-ui/icons/BusinessRounded";
import EmojiObjectsRoundedIcon from "@material-ui/icons/EmojiObjectsRounded";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import { useSetup } from "../hooks/useSetup";

import {
  KoraxOnboardingShell,
  KoraxOnboardingContent,
  KoraxOnboardingFooter
} from "../../../components/KoraxUI/Onboarding";

const useStyles = makeStyles(theme => ({
  hero: {
    width: "100%",
    maxWidth: 820,
    margin: "0 auto",
    boxSizing: "border-box"
  },

  successCard: {
    padding: 24,
    borderRadius: 20,
    border: "1px solid rgba(21,94,239,.16)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg,rgba(21,94,239,.14),rgba(11,165,236,.06))"
        : "linear-gradient(135deg,#EFF6FF,#F8FBFF)",
    boxShadow: "0 16px 38px rgba(15,23,42,.08)"
  },

  successHeader: {
    display: "flex",
    alignItems: "center",
    gap: 14
  },

  successIcon: {
    width: 52,
    height: 52,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#155EEF,#0BA5EC)",
    boxShadow: "0 12px 26px rgba(21,94,239,.24)"
  },

  successIconSvg: {
    fontSize: 30
  },

  successTitle: {
    color: theme.palette.text.primary,
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.25
  },

  successText: {
    marginTop: 4,
    color: theme.palette.text.secondary,
    fontSize: 12.5,
    lineHeight: 1.55
  },

  checklist: {
    marginTop: 22,
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: 12,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  item: {
    minHeight: 84,
    padding: 14,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "flex-start",
    gap: 11,
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper
  },

  itemIcon: {
    width: 34,
    height: 34,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    color: "#155EEF",
    background: "rgba(21,94,239,.09)"
  },

  itemTitle: {
    color: theme.palette.text.primary,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.3
  },

  itemText: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: 10.8,
    lineHeight: 1.4
  },

  nextCard: {
    marginTop: 16,
    padding: 17,
    display: "flex",
    alignItems: "center",
    gap: 13,
    borderRadius: 14,
    border: "1px solid rgba(22,163,74,.18)",
    background: "rgba(22,163,74,.06)"
  },

  whatsappIcon: {
    flexShrink: 0,
    color: "#16A34A",
    fontSize: 28
  },

  nextTitle: {
    color: theme.palette.text.primary,
    fontSize: 12.5,
    fontWeight: 850
  },

  nextText: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    lineHeight: 1.45
  }
}));

export default function ReadyStep() {
  const classes = useStyles();

  const {
    company,
    nextStep,
    previousStep
  } = useSetup();

  const companyName =
    company?.name ||
    company?.companyName ||
    "Sua empresa";

  const items = [
    {
      icon: <BusinessRoundedIcon fontSize="small" />,
      title: "Empresa configurada",
      text: `${companyName} já faz parte do contexto da sua IA.`
    },
    {
      icon: <EmojiObjectsRoundedIcon fontSize="small" />,
      title: "IA preparada",
      text: "As informações e o objetivo do agente já foram processados."
    },
    {
      icon: <ChatRoundedIcon fontSize="small" />,
      title: "Teste concluído",
      text: "Você já conferiu como sua inteligência responde."
    }
  ];

  return (
    <KoraxOnboardingShell>
      <KoraxOnboardingContent
        eyebrow="Agente preparado"
        title="Sua IA está pronta"
        description="A etapa de preparação foi concluída. Agora falta apenas conectar o canal que colocará sua inteligência em contato com os clientes."
      >
        <div className={classes.hero}>
          <div className={classes.successCard}>
            <div className={classes.successHeader}>
              <div className={classes.successIcon}>
                <CheckCircleRoundedIcon
                  className={classes.successIconSvg}
                />
              </div>

              <div>
                <Typography className={classes.successTitle}>
                  Tudo certo com a preparação
                </Typography>

                <Typography className={classes.successText}>
                  Seu agente já conhece a empresa, entende sua missão e está preparado para começar.
                </Typography>
              </div>
            </div>

            <div className={classes.checklist}>
              {items.map(item => (
                <div
                  key={item.title}
                  className={classes.item}
                >
                  <div className={classes.itemIcon}>
                    {item.icon}
                  </div>

                  <div>
                    <Typography className={classes.itemTitle}>
                      {item.title}
                    </Typography>

                    <Typography className={classes.itemText}>
                      {item.text}
                    </Typography>
                  </div>
                </div>
              ))}
            </div>

            <div className={classes.nextCard}>
              <WhatsAppIcon className={classes.whatsappIcon} />

              <div>
                <Typography className={classes.nextTitle}>
                  Último passo: conectar seu WhatsApp
                </Typography>

                <Typography className={classes.nextText}>
                  A conexão será feita com um QR Code e ativará seu agente no número da empresa.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </KoraxOnboardingContent>

      <KoraxOnboardingFooter
        onBack={previousStep}
        onNext={nextStep}
        nextLabel="Conectar meu WhatsApp"
      />
    </KoraxOnboardingShell>
  );
}
